import type {
  ComponentPublicInstance,
  Ref,
  VNode,
} from 'vue'
import {
  createVNode,
  ref,
  render,
  watch,
} from 'vue'
import MessageVue from './Message.vue'

interface MessageOptions {
  message: string;
  type: 'null' | 'error' | 'success' | 'warning' | 'loading';
  duration?: number;
  showClose?: boolean;
  option?: {
    title?: string;
    callback?: VoidFunction;
    layout?: 'between' | 'inline';
  };
  offset?: number;
}

type ComponentInstance = ComponentPublicInstance<{
  visible: boolean;
  hide: () => void;
  setPosition: (y: number) => void;
}>;

const messageQueue: Ref<ComponentInstance[]> = ref([])

type OptionsType = MessageOptions | string | (() => string);

const isObject = (v: OptionsType): v is MessageOptions => typeof v === 'object' && v !== null

const isString = (v: OptionsType): v is string => typeof v === 'string'

const isFunc = (v: OptionsType): v is () => string => typeof v === 'function'

const renderMessageElement = (vm: VNode) => {
  const container = document.createElement('div')
  render(vm, container)
  return container.firstChild as Element
}

const mountMessageToBody = (el: Element) => {
  document.body.appendChild(el)
}
const unMountFromBody = (el: Element) => {
  document.body.removeChild(el)
}

const useMessage = (options: OptionsType) => {
  if (!options) return {}
  const defaultMessageOptions: MessageOptions = {
    showClose: false,
    message: '',
    type: 'null',
    duration: 3000,
  }

  if (isObject(options) && !options.message) {
    throw new Error('options require \'message\' property.')
  }
  if (isString(options)) {
    defaultMessageOptions.message = options
  } else if (isFunc(options)) {
    defaultMessageOptions.message = options()
  } else {
    Object.assign(defaultMessageOptions, options)
  }

  // 创建虚拟dom
  const vm = createVNode(MessageVue, {
    ...defaultMessageOptions,
  })
  // 虚拟dom挂载到body上
  const el = renderMessageElement(vm)

  // message组件的vue实例， 通过render方法后, proxy获取组件实例
  const component = vm.component?.proxy as ComponentInstance
  const handleHideMessage = (v: boolean) => {
    if (!v) {
      unMountFromBody(el)
      messageQueue.value = messageQueue.value.filter((c) => c !== component)
    }
  }

  watch(() => component.visible, handleHideMessage)
  messageQueue.value.forEach((c) => c.$.exposed?.hide())
  messageQueue.value = []
  mountMessageToBody(el)
  messageQueue.value.push(component)

  return {
    close: () => {
      component.$.exposed?.hide()
    },
  }
}

export default useMessage
